import { useEffect, useState } from 'react';
import { Panel } from 'react-resizable-panels';

import { RESOLUTIONS, durationLabel, type RENDER_PRESETS, type VideoMode } from '@ai-studio/shared';

import { askAssistant } from '../../lib/ask-ai.js';
import { useCommands } from '../../lib/shortcuts/index.js';
import { planPrompt, timelineAt } from '../../lib/video.js';
import { useGenerationFeed } from '../generation/hooks.js';
import { acquireVideoPolling, useVideo } from '../../state/video.js';
import { PanelHeader, SaveIndicator } from '../scene/SceneTab.js';
import { MenuButton, NumberField, SelectField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { Handle, SplitGroup } from '../ui/layout.js';
import { ShotInspector } from './ShotInspector.js';
import { Storyboard } from './Storyboard.js';
import { StoryPanel } from './StoryPanel.js';
import { VideoTimeline } from './VideoTimeline.js';
import './video.css';

/**
 * Video Studio: storyboard films and cutscenes with the Assistant as writer and director, and local models
 * (or the engine) making every shot, voice, sound and song. Left: story (bible, characters, locations).
 * Centre: the storyboard grid. Right: the selected shot. Bottom: player + timeline. Top: prompt, length,
 * mode, resolution and the render buttons. Every piece is a generation request (approve / review in the
 * Generations inbox or right here in the inspector).
 */

const MODES: { value: VideoMode; label: string }[] = [
  { value: 'ai', label: 'AI video (local models)' },
  { value: 'engine', label: 'In-engine (scenes + cameras)' },
  { value: 'mixed', label: 'Mixed' },
];

function StudioBar() {
  const doc = useVideo((s) => s.doc);
  const path = useVideo((s) => s.path);
  const saveState = useVideo((s) => s.saveState);
  const director = useVideo((s) => s.director);
  const estimate = useVideo((s) => s.estimate);
  const errors = useVideo((s) => s.errors);
  const videos = useVideo((s) => s.videos);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  if (!doc || !path) return null;
  const s = useVideo.getState();
  const total = s.timeline().total;
  const res = `${doc.width}x${doc.height}`;
  const render = async (preset: keyof typeof RENDER_PRESETS) => {
    setBusy(true);
    await s.render({ preset });
    setBusy(false);
  };
  return (
    <div className="vs-bar">
      <div className="vs-bar-row">
        <MenuButton className="ghost small vs-file" title="Open another video" items={() => [...videos.map((v) => ({ id: v, label: v, icon: 'file' as const, run: () => void s.open(v) })), { id: 'sep', label: '', separator: true }, { id: 'new', label: 'New video…', icon: 'plus' as const, run: () => s.close() }]}>
          <Icon name="camera" size={13} /> {path} <Icon name="chevronDown" size={11} />
        </MenuButton>
        <SaveIndicator state={saveState} />
        <span className="muted vs-length" title="Timeline length / target length">
          {durationLabel(total)} / {durationLabel(doc.targetLength)}
        </span>
        {estimate && (
          <span className="muted vs-est" title="Rough render time on this PC (engine/comfy/README.md)">
            render ≈ {durationLabel(estimate.final)} (animatic {durationLabel(estimate.animatic)})
          </span>
        )}
        {errors.length > 0 && (
          <span className="vs-errors" title={errors.join('\n')}>
            <Icon name="warning" size={12} /> {errors.length} problem{errors.length === 1 ? '' : 's'}
          </span>
        )}
        <span className="bar-spacer" />
        {director ? (
          <button type="button" className="ghost small" onClick={() => void s.stop()} title={`Rendering since ${new Date(director.startedAt).toLocaleTimeString()}: ${director.groups.join(', ')}. Stop starting new pieces (queued ones stay in the queue).`}>
            <span className="vs-spinner" /> Rendering… Stop
          </button>
        ) : null}
        <button type="button" className="ghost small" disabled={busy || errors.length > 0} onClick={() => void render('animatic')} title="Character sheets, keyframes and fast animatic clips (Causal Forcing, ~2 min per shot)">
          <Icon name="frames" size={13} /> Render animatic
        </button>
        <button type="button" className="ghost small" disabled={busy || errors.length > 0} onClick={() => void render('final')} title="Final shots (Wan 2.2), engine recordings, voice-overs, lip sync and post">
          <Icon name="camera" size={13} /> Render final
        </button>
        <button type="button" className="ghost small" disabled={busy || errors.length > 0} onClick={() => void render('audio')} title="Voices, sound effects, ambience and music with lyrics">
          <Icon name="audio" size={13} /> Audio
        </button>
        <button type="button" className="primary small" disabled={busy || errors.length > 0} onClick={() => void render('assemble')} title="Join the shots, mix dialogue / music / SFX, loudness, subtitles">
          <Icon name="layers" size={13} /> Assemble
        </button>
      </div>
      <form
        className="vs-bar-row"
        onSubmit={(e) => {
          e.preventDefault();
          void (async () => {
            setBusy(true);
            await s.flush();
            if (await askAssistant(planPrompt(path, doc, prompt))) setPrompt('');
            setBusy(false);
          })();
        }}
      >
        <Icon name="sparkles" size={15} className="ai-glyph" />
        <input className="textfield vs-prompt" value={prompt} placeholder={doc.prompt ? `About: ${doc.prompt}` : 'What is the video about? (story, mood, characters)…'} onChange={(e) => setPrompt(e.target.value)} aria-label="Video prompt" />
        <span className="muted">Length (s)</span>
        <div className="vs-num">
          <NumberField value={doc.targetLength} onChange={(v) => s.edit((d) => ({ ...d, targetLength: v }))} min={5} max={14400} step={5} integer title="Target length in seconds" />
        </div>
        <SelectField value={doc.mode} options={MODES} onChange={(v) => s.edit((d) => ({ ...d, mode: v as VideoMode }))} />
        <SelectField
          value={RESOLUTIONS.some((r) => `${r.width}x${r.height}` === res) ? res : undefined}
          placeholder={res}
          options={RESOLUTIONS.map((r) => ({ value: `${r.width}x${r.height}`, label: r.label }))}
          onChange={(v) => {
            const [w, h] = v.split('x').map(Number);
            s.edit((d) => ({ ...d, width: w!, height: h! }));
          }}
        />
        <div className="vs-num">
          <NumberField value={doc.fps} onChange={(v) => s.edit((d) => ({ ...d, fps: v }))} min={8} max={60} step={1} integer title="Frames per second of the edit" />
        </div>
        <button type="submit" className="primary small" disabled={busy}>
          <Icon name="sparkles" size={13} /> Plan with Assistant
        </button>
      </form>
    </div>
  );
}

function CreateVideo() {
  const videos = useVideo((s) => s.videos);
  const error = useVideo((s) => s.error);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [length, setLength] = useState(60);
  const [mode, setMode] = useState<VideoMode>('ai');
  const [res, setRes] = useState('1280x720');
  const s = useVideo.getState();
  return (
    <div className="vs-empty">
      <Icon name="camera" size={26} />
      <strong>Video Studio</strong>
      <span className="muted">Storyboard a cutscene or a film. The Assistant writes it; local models (or the engine) make every shot, voice, sound and song.</span>
      {error && <span className="gen-error">{error}</span>}
      {videos.length > 0 && (
        <div className="scene-list">
          {videos.map((v) => (
            <button key={v} type="button" className="ghost small" onClick={() => void s.open(v)}>
              <Icon name="camera" size={12} /> {v}
            </button>
          ))}
        </div>
      )}
      <form
        className="vs-create"
        onSubmit={(e) => {
          e.preventDefault();
          const [w, h] = res.split('x').map(Number);
          void s.create({ name: name.trim() || 'New video', mode, targetLength: length, width: w!, height: h!, ...(prompt.trim() ? { prompt: prompt.trim() } : {}) });
        }}
      >
        <input className="textfield" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Intro cutscene)" aria-label="Video name" />
        <textarea className="textfield" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="What is it about? The Assistant plans the story from this." aria-label="Video prompt" />
        <div className="vs-create-row">
          <span className="muted">Length (s)</span>
          <div className="vs-num">
            <NumberField value={length} onChange={setLength} min={5} max={14400} step={5} integer />
          </div>
          <SelectField value={mode} options={MODES} onChange={(v) => setMode(v as VideoMode)} />
          <SelectField value={res} options={RESOLUTIONS.map((r) => ({ value: `${r.width}x${r.height}`, label: r.label }))} onChange={setRes} />
        </div>
        <button type="submit" className="primary small">
          <Icon name="plus" size={13} /> Create video
        </button>
      </form>
    </div>
  );
}

export function VideoTab({ active }: { active: boolean }) {
  const doc = useVideo((s) => s.doc);
  const path = useVideo((s) => s.path);
  useGenerationFeed(active);

  useEffect(() => {
    void useVideo.getState().listVideos();
  }, []);
  useEffect(() => {
    if (!active || !path) return;
    return acquireVideoPolling();
  }, [active, path]);
  useEffect(() => {
    if (!active && useVideo.getState().playing) useVideo.getState().setPlaying(false);
  }, [active]);

  const s = useVideo.getState;
  const step = (delta: number) => {
    const { entries } = s().timeline();
    const at = timelineAt(entries, s().time);
    const next = entries[Math.max(0, Math.min(entries.length - 1, (at?.index ?? 0) + delta))];
    if (next) {
      s().setTime(next.start);
      s().select({ kind: 'shot', id: next.shotId });
    }
  };
  const hasDoc = () => Boolean(s().doc);
  useCommands(
    [
      { id: 'video.play', label: 'Play / pause the video', category: 'Video', context: 'video', keys: ['Space'], icon: 'play', enabled: hasDoc, run: () => s().setPlaying(!s().playing) },
      { id: 'video.start', label: 'Go to start', category: 'Video', context: 'video', keys: ['Home'], enabled: hasDoc, run: () => s().setTime(0) },
      { id: 'video.end', label: 'Go to end', category: 'Video', context: 'video', keys: ['End'], enabled: hasDoc, run: () => s().setTime(s().timeline().total) },
      { id: 'video.prev-shot', label: 'Previous shot', category: 'Video', context: 'video', keys: [','], enabled: hasDoc, run: () => step(-1) },
      { id: 'video.next-shot', label: 'Next shot', category: 'Video', context: 'video', keys: ['.'], enabled: hasDoc, run: () => step(1) },
      { id: 'video.undo', label: 'Undo storyboard edit', category: 'Video', context: 'video', keys: ['Ctrl+Z'], icon: 'undo', enabled: () => s().undoStack.length > 0, run: () => s().undo() },
      { id: 'video.redo', label: 'Redo storyboard edit', category: 'Video', context: 'video', keys: ['Ctrl+Shift+Z', 'Ctrl+Y'], icon: 'redo', enabled: () => s().redoStack.length > 0, run: () => s().redo() },
      { id: 'video.render-shot', label: 'Render the selected shot (final)', category: 'Video', context: 'video', keys: ['Ctrl+Enter'], icon: 'camera', enabled: () => s().selection.kind === 'shot', whileTyping: true, run: () => void s().render({ preset: 'final', shot: (s().selection as { id: string }).id }) },
    ],
    [],
  );

  if (!doc || !path) return <div className="tab-body tab-video">{<CreateVideo />}</div>;

  return (
    <div className="tab-body tab-video">
      <StudioBar />
      <SplitGroup id="video-v" orientation="vertical" fallback={{ top: 68, timeline: 32 }} className="vs-split">
        <Panel id="top" minSize={200}>
          <SplitGroup id="video-h" orientation="horizontal" fallback={{ story: 22, board: 50, inspector: 28 }}>
            <Panel id="story" minSize={200} className="pane">
              <PanelHeader title="Story" />
              <StoryPanel />
            </Panel>
            <Handle orientation="vertical" />
            <Panel id="board" minSize={320} className="pane pane-center">
              <PanelHeader title="Storyboard" />
              <Storyboard />
            </Panel>
            <Handle orientation="vertical" />
            <Panel id="inspector" minSize={260} className="pane">
              <PanelHeader title="Shot" />
              <ShotInspector />
            </Panel>
          </SplitGroup>
        </Panel>
        <Handle orientation="horizontal" />
        <Panel id="timeline" minSize={120} className="pane">
          <VideoTimeline active={active} />
        </Panel>
      </SplitGroup>
    </div>
  );
}
